import React, { ReactElement, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { request, useFormikHandles, createMarkup, createUrl } from 'app/utils';
import { Box, Button, Flex, HelperText, Input, Label, Loading, Page, Section, Select, Typography } from 'components';
import { useSchool } from 'features/school';
import { useMenu } from 'features/menu';
import Alert, { useAlert } from 'features/alert';
import { GenerateFormItem } from 'features/account/types';
import { AxiosError } from 'axios';
import { useAuth } from 'features/auth';
import { useNavigate } from 'react-router-dom';

const ChangePasswordView = (): ReactElement => {
  const [loadingEdit, setLoadingEdit] = useState(false);

  const { apiGatewayUrl, tenantUuid, schoolUuid } = useSchool();
  const { clearBackPathname } = useMenu();
  const { setAlert, clearAlert } = useAlert();
  const { sso } = useAuth();
  const navigate = useNavigate();

  const initialValues: Record<string, any> = {
    oldPassword: '',
    newPassword: '',
    repeatNewPassword: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      oldPassword: Yup.string().required('Você precisa informar sua senha atual'),
      newPassword: Yup.string().required('Você precisa informar uma nova senha'),
      // .matches(
      //   /^(?=.*[A-Za-z])(?=.*\d)(?=.*[%?!@#$&*])[A-Za-z\d%?!@#$&*]{8,}$/,
      //   'A senha deve conter: mínimo 8 caracteres, 1 letra maiúscula, 1 letra minúscula, 1 número e um caractere especial'
      // ),
      repeatNewPassword: Yup.string()
        .required('Você precisa confirmar sua nova senha')
        .oneOf([Yup.ref('newPassword'), null], 'A confirmação está diferente da nova senha'),
    }),
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      setLoadingEdit(true);

      request({
        url: `/api/v1/User/PasswordChange`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          tenantUuid,
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        },
      })
        .then(() => {
          setLoadingEdit(false);
          setAlert({ type: 'success', message: 'Seus dados foram atualizados com sucesso.' });
        })
        .catch((error: AxiosError<{ message: string }>) => {
          setAlert({ type: 'error', message: error?.response?.data?.message || 'Ocorreu um erro ao atualizar seus dados.' });
        })
        .finally(() => {
          setLoadingEdit(false);
        });
    },
  });

  const { formikHandleFocus, formikHandleBlur, formikHandleChange } = useFormikHandles(formik);

  useEffect(() => {
    if (sso) navigate(createUrl('dashboard'));

    return () => {
      clearBackPathname();
      clearAlert();
    };
  }, []);

  const generateFormFirst: GenerateFormItem[][] = [
    [
      {
        name: 'oldPassword',
        label: 'Senha atual',
        formType: 'input',
        inputType: 'text',
        maxLength: 50,
        width: { m: '100%', d: 350 },
      },
    ],
    [
      {
        name: 'newPassword',
        label: 'Nova senha',
        formType: 'input',
        inputType: 'text',
        maxLength: 50,
        width: { m: '100%', d: 350 },
        rightText:
          schoolUuid === '76cebc79-bf6c-4144-bab2-947362f3f8c4'
            ? 'Mínimo 6 caracteres.'
            : 'Mínimo 8 caracteres, 1 letra maiúscula, 1 letra minúscula,<br />1 número e um caractere especial.',
      },
    ],
    [
      {
        name: 'repeatNewPassword',
        label: 'Confirmar nova senha',
        formType: 'input',
        inputType: 'text',
        maxLength: 50,
        width: { m: '100%', d: 350 },
        rightText:
          schoolUuid === '76cebc79-bf6c-4144-bab2-947362f3f8c4'
            ? 'Mínimo 6 caracteres.'
            : 'Mínimo 8 caracteres, 1 letra maiúscula, 1 letra minúscula,<br />1 número e um caractere especial.',
      },
    ],
  ];

  return (
    <Page title="Meus cursos">
      {loadingEdit && <Loading alpha />}

      <form onSubmit={formik.handleSubmit}>
        <Flex py={{ m: 30, t: 60, d: 90 }} bg="gray_200" flexGrow={0}>
          <Section flexDirection={{ m: 'column', d: 'row' }} justifyContent="space-between" flexWrap="nowrap">
            <Flex flexGrow={1} flexDirection="column">
              <Typography type="type4" fontWeight="bold" upper>
                Trocar senha
              </Typography>

              <Typography type="type1" mt={1} mb={2}>
                Solicite a troca de sua senha.
              </Typography>

              {generateFormFirst.map((rowItem, rowIndex) => {
                return (
                  <Flex flexWrap="nowrap" alignItems="flex-start">
                    {rowItem.map((item, index) =>
                      item.hide ? (
                        <></>
                      ) : (
                        <Flex flexGrow={{ m: 1, d: 0 }} flexDirection={{ m: 'column', d: 'row' }} flexWrap={{ m: 'wrap', d: 'nowrap' }}>
                          {rowItem.map((item, index) =>
                            item.hide ? (
                              <></>
                            ) : (
                              <Flex
                                key={`form-item-${item.name}`}
                                flexGrow={{ m: 1, d: 0 }}
                                // flexDirection={{ m: 'column', d: 'row' }}
                                // flexWrap={{ m: 'wrap', d: 'nowrap' }}
                                // width={{ m: '100%', d: 'inherit' }}
                              >
                                <Flex
                                  flexDirection="column"
                                  mt={4}
                                  ml={{ d: index ? 3 : 0 }}
                                  width={item && item.width ? item.width : 'inherit'}
                                  maxWidth={item && item.maxWidth ? item.maxWidth : 'inherit'}
                                  flexShrink={0}
                                  flexGrow={1}
                                >
                                  <Flex justifyContent="space-between">
                                    <Flex flexGrow={0}>
                                      <Typography type="type1" fontWeight="bold" upper>
                                        <Label htmlFor={`item-${item.name}`}>{item.label}</Label>
                                      </Typography>
                                    </Flex>
                                  </Flex>

                                  <Flex flexDirection="column">
                                    <Flex mt={1} flexWrap="nowrap" alignItems="center">
                                      <Flex flexGrow={1} flexWrap="nowrap" alignItems="center">
                                        {item.before && item.before.length ? (
                                          item.before.map((itemBefore, indexBefore) => {
                                            return (
                                              <Box
                                                flexGrow={0}
                                                key={`form-itemBefore-${itemBefore.name}`}
                                                width={itemBefore.width ? itemBefore.width : 'inherit'}
                                                maxWidth={itemBefore.maxWidth ? itemBefore.maxWidth : 'inherit'}
                                                mr={2}
                                              >
                                                {itemBefore.formType === 'input' && (
                                                  <Input
                                                    id={`item-${itemBefore.name}`}
                                                    name={itemBefore.name}
                                                    type={itemBefore.inputType}
                                                    onChange={formikHandleChange}
                                                    onFocus={formikHandleFocus}
                                                    onBlur={formikHandleBlur}
                                                    value={
                                                      itemBefore.mask
                                                        ? itemBefore.mask(formik.values[itemBefore.name])
                                                        : formik.values[itemBefore.name]
                                                    }
                                                    maxLength={itemBefore.maxLength}
                                                    data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                                    placeholder={itemBefore.placeholder || ''}
                                                    data-removeArrows={!!itemBefore.inputNumberRemoveArrows}
                                                  />
                                                )}

                                                {itemBefore.formType === 'select' && (
                                                  <Select
                                                    id={`item-${itemBefore.name}`}
                                                    name={itemBefore.name}
                                                    data-error={formik.touched[itemBefore.name] && formik.errors[itemBefore.name]}
                                                    onChange={formikHandleChange}
                                                    disabled={itemBefore.disabled}
                                                    defaultValue={formik.values[itemBefore.name]}
                                                  >
                                                    {!itemBefore.selectRemoveFirstOption && (
                                                      <option value={itemBefore.selectFirstValue} hidden>
                                                        {itemBefore.selectFirstLabel || ''}
                                                      </option>
                                                    )}
                                                    {itemBefore.selectOptions &&
                                                      itemBefore.selectOptions.map((optionItem, index) => (
                                                        <option
                                                          key={`option-item-${itemBefore.name}-${index.toString()}`}
                                                          // selected={formik.values[itemBefore.name] === optionItem.value}
                                                          value={optionItem.value}
                                                        >
                                                          {optionItem.label}
                                                        </option>
                                                      ))}
                                                  </Select>
                                                )}
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          <></>
                                        )}

                                        {item.formType === 'input' && (
                                          <Input
                                            id={`item-${item.name}`}
                                            name={item.name}
                                            type={item.inputType}
                                            onChange={formikHandleChange}
                                            onFocus={formikHandleFocus}
                                            onBlur={formikHandleBlur}
                                            value={item.mask ? item.mask(formik.values[item.name]) : formik.values[item.name]}
                                            maxLength={item.maxLength}
                                            data-error={formik.touched[item.name] && formik.errors[item.name]}
                                            placeholder={item.placeholder || ''}
                                            data-removeArrows={!!item.inputNumberRemoveArrows}
                                          />
                                        )}

                                        {item.formType === 'select' && (
                                          <Select
                                            id={`item-${item.name}`}
                                            name={item.name}
                                            data-error={formik.touched[item.name] && formik.errors[item.name]}
                                            onChange={formikHandleChange}
                                            disabled={item.disabled}
                                            defaultValue={formik.values[item.name]}
                                          >
                                            {!item.selectRemoveFirstOption && (
                                              <option value={item.selectFirstValue} hidden>
                                                {item.selectFirstLabel}
                                              </option>
                                            )}
                                            {item.selectOptions &&
                                              item.selectOptions.map((optionItem, index) => (
                                                <option
                                                  key={`option-item-${item.name}-${index.toString()}`}
                                                  // selected={formik.values[item.name] === optionItem.value}
                                                  value={optionItem.value}
                                                >
                                                  {optionItem.label}
                                                </option>
                                              ))}
                                          </Select>
                                        )}

                                        {item.after && item.after.length ? (
                                          item.after.map((itemAfter, indexBefore) => {
                                            return (
                                              <Box
                                                flexGrow={0}
                                                key={`form-itemAfter-${itemAfter.name}`}
                                                width={itemAfter.width ? itemAfter.width : 'inherit'}
                                                maxWidth={itemAfter.maxWidth ? itemAfter.maxWidth : 'inherit'}
                                                ml={2}
                                              >
                                                {itemAfter.formType === 'input' && (
                                                  <Input
                                                    id={`item-${itemAfter.name}`}
                                                    name={itemAfter.name}
                                                    type={itemAfter.inputType}
                                                    onChange={formikHandleChange}
                                                    onFocus={formikHandleFocus}
                                                    onBlur={formikHandleBlur}
                                                    value={
                                                      itemAfter.mask ? itemAfter.mask(formik.values[itemAfter.name]) : formik.values[itemAfter.name]
                                                    }
                                                    maxLength={itemAfter.maxLength}
                                                    data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                                    placeholder={itemAfter.placeholder || ''}
                                                    data-removeArrows={!!itemAfter.inputNumberRemoveArrows}
                                                  />
                                                )}

                                                {itemAfter.formType === 'select' && (
                                                  <Select
                                                    id={`item-${itemAfter.name}`}
                                                    name={itemAfter.name}
                                                    data-error={formik.touched[itemAfter.name] && formik.errors[itemAfter.name]}
                                                    onChange={formikHandleChange}
                                                    disabled={itemAfter.disabled}
                                                    defaultValue={formik.values[itemAfter.name]}
                                                  >
                                                    {!itemAfter.selectRemoveFirstOption && (
                                                      <option value={itemAfter.selectFirstValue} hidden>
                                                        {itemAfter.selectFirstLabel || ''}
                                                      </option>
                                                    )}
                                                    {itemAfter.selectOptions &&
                                                      itemAfter.selectOptions.map((optionItem, index) => (
                                                        <option
                                                          key={`option-item-${itemAfter.name}-${index.toString()}`}
                                                          // selected={formik.values[itemAfter.name] === optionItem.value}
                                                          value={optionItem.value}
                                                        >
                                                          {optionItem.label}
                                                        </option>
                                                      ))}
                                                  </Select>
                                                )}
                                              </Box>
                                            );
                                          })
                                        ) : (
                                          <></>
                                        )}
                                      </Flex>
                                    </Flex>

                                    {formik.touched[item.name] && formik.errors[item.name] ? (
                                      <HelperText type="error">{formik.errors[item.name]}</HelperText>
                                    ) : null}
                                  </Flex>
                                </Flex>

                                {Boolean(item.rightText) && (
                                  <Flex flexGrow={0} alignItems="center" height={38} mt={{ m: 1, d: '43px' }}>
                                    <Typography type="type0" ml={{ d: 3 }}>
                                      <div dangerouslySetInnerHTML={createMarkup(item.rightText)} />
                                    </Typography>
                                  </Flex>
                                )}
                              </Flex>
                            )
                          )}
                        </Flex>
                      )
                    )}
                  </Flex>
                );
              })}

              <Flex mt={3}>
                {Boolean(Object.values(formik.errors).length) && (
                  <Flex bg="alert_400" p={3} borderRadius={4}>
                    <Typography type="type2" fontWeight="bold" color="white">
                      Preencha corretamente os campos acima em destaque.
                    </Typography>
                  </Flex>
                )}

                <Alert />
              </Flex>

              <Flex mt={3}>
                <Button disabled={Boolean(Object.values(formik.errors).length)} type="submit">
                  Salvar
                </Button>
              </Flex>
            </Flex>

            <Flex width={{ d: 420 }} ml={{ d: 60 }} flexShrink={0} flexGrow={0} position="relative">
              <Flex position={{ d: 'absolute' }} mt={{ d: -295 }} width={1}></Flex>
            </Flex>
          </Section>
        </Flex>
      </form>
    </Page>
  );
};

export default ChangePasswordView;
